/**
 * v7.98 — Pausa GLOBAL do Delivery (multi-device, tempo real).
 *
 * Antes: `deliveryPaused` vivia só no Dexie local do aparelho do admin —
 * os clientes nunca ficavam sabendo e continuavam finalizando pedidos.
 *
 * Agora, 3 camadas:
 *  1. Backend (Mongo `app_config.deliveryPaused`) = AUTORIDADE. A rota
 *     /api/integrations/mp/create-pix rejeita com 423 quando pausado.
 *  2. Firebase RTDB `{group}/settings/deliveryPaused` = propagação em
 *     tempo real para todos os aparelhos (cardápio do cliente escuta).
 *  3. Polling de segurança (30s) direto no backend, caso Firebase caia.
 */
import db from '@/db';
import { getFirebaseApp, getDeviceGroup } from '@/lib/firebaseSync';
import { getDatabase, ref, set, onValue } from 'firebase/database';
import { saveServerConfig } from '@/lib/serverConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Lê status no backend. Retorna true/false, ou null se rede falhou. */
export async function fetchDeliveryPaused() {
  if (!API_URL) return null;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  try {
    const r = await fetch(`${API_URL}/api/config/delivery-status`, { signal: controller.signal });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const body = await r.json();
    return !!body.paused;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Aplica a pausa em TODAS as camadas (chamado pelo admin).
 * Retorna { serverOk, firebaseOk } para a UI informar falhas.
 */
export async function setDeliveryPausedEverywhere(next) {
  const value = !!next;
  // 1. SERVIDOR PRIMEIRO (autoridade do checkout). Se falhar, NÃO propaga
  //    ao Firebase — evita estado inconsistente (cliente aberto/backend fechado).
  let serverOk = false;
  try {
    await saveServerConfig({ deliveryPaused: value });
    serverOk = true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[PAUSE] falha ao gravar no servidor:', err?.message || err);
    return { serverOk: false, firebaseOk: false };
  }
  try { await db.config.put({ key: 'deliveryPaused', value }); } catch {}
  let firebaseOk = false;
  try {
    const app = getFirebaseApp();
    if (app) {
      const group = await getDeviceGroup();
      await set(ref(getDatabase(app), `${group}/settings/deliveryPaused`), {
        value,
        updatedAt: new Date().toISOString(),
      });
      firebaseOk = true;
      // eslint-disable-next-line no-console
      console.log(`[PAUSE] propagado: server=OK firebase=OK value=${value}`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[PAUSE] falha ao propagar via Firebase (poll de 30s cobre):', err?.message || err);
  }
  return { serverOk, firebaseOk };
}

/**
 * Escuta a pausa em tempo real (Firebase) + polling de segurança (backend).
 * `cb(paused: boolean)` é chamado a cada mudança. Retorna unsubscribe.
 */
export function subscribeDeliveryPaused(cb) {
  let disposed = false;
  let fbUnsub = null;
  let retryTimer = null;

  const tryAttach = async () => {
    if (disposed || fbUnsub) return;
    const app = getFirebaseApp();
    if (!app) {
      retryTimer = setTimeout(tryAttach, 3000);
      return;
    }
    try {
      const group = await getDeviceGroup();
      const node = ref(getDatabase(app), `${group}/settings/deliveryPaused`);
      fbUnsub = onValue(node, (snap) => {
        const v = snap.val();
        if (disposed) return;
        if (v && typeof v === 'object' && 'value' in v) cb(!!v.value);
      }, () => { /* permission/network: polling cobre */ });
    } catch {
      retryTimer = setTimeout(tryAttach, 5000);
    }
  };
  tryAttach();

  const poll = setInterval(async () => {
    const v = await fetchDeliveryPaused();
    if (v !== null && !disposed) cb(v);
  }, 60000);

  return () => {
    disposed = true;
    clearInterval(poll);
    if (retryTimer) clearTimeout(retryTimer);
    try { if (fbUnsub) fbUnsub(); } catch {}
  };
}
